<script lang="ts">
import { computed, defineComponent, ref, watch, onMounted } from 'vue';
import { onKeyDown, useMagicKeys } from '@vueuse/core';
import { Tools } from '@/src/store/tools/types';
import ControlButton from '@/src/components/ControlButton.vue';
import ItemGroup from '@/src/components/ItemGroup.vue';
import GroupableItem from '@/src/components/GroupableItem.vue';
import { useToolStore } from '@/src/store/tools';
import MenuControlButton from '@/src/components/MenuControlButton.vue';
import CropControls from '@/src/components/tools/crop/CropControls.vue';
import ResetViews from '@/src/components/tools/ResetViews.vue';
import RulerControls from '@/src/components/RulerControls.vue';
import RectangleControls from '@/src/components/RectangleControls.vue';
import PolygonControls from '@/src/components/PolygonControls.vue';
import WindowLevelControls from '@/src/components/tools/windowing/WindowLevelControls.vue';
import { actionToKey } from '@/src/composables/useKeyboardShortcuts';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { useViewStore } from '@/src/store/views';
import { useWindowingStore } from '@/src/store/view-configs/windowing';

declare global {
  interface Window {
    setCurrentTool: (tool: Tools) => void;
    repairInteractions: () => void;
    disableAutoInteractions: () => void;
    enableAutoInteractions: () => void;
    handleFlutterUpdate: (data: { type: string; zoom?: number }) => void;
    sendZoomToFlutter?: (zoomValue: number) => void;
    setWindowLevel: (width: number, level: number) => void;
    getCurrentWindowLevel: () => { width: number; level: number };
  }
}

export default defineComponent({
  components: {
    ControlButton,
    MenuControlButton,
    ItemGroup,
    GroupableItem,
    CropControls,
    ResetViews,
    RulerControls,
    RectangleControls,
    PolygonControls,
    WindowLevelControls,
  },
  setup() {
    const toolStore = useToolStore();
    const viewStore = useViewStore();
    const windowingStore = useWindowingStore();
    const { currentImageID } = useCurrentImage();

    const noCurrentImage = computed(() => !currentImageID.value);
    const currentTool = computed(() => toolStore.currentTool);
    const isObliqueLayout = computed(() => {
      if (!viewStore.activeView) return false;
      const view = viewStore.viewByID[viewStore.activeView];
      return view.type === 'Oblique';
    });

    const paintMenu = ref(false);
    const cropMenu = ref(false);
    const windowingMenu = ref(false);

    onKeyDown('Escape', () => {
      paintMenu.value = false;
      cropMenu.value = false;
      windowingMenu.value = false;
    });

    const keys = useMagicKeys();
    const enableTempCrosshairs = computed(
      () => keys[actionToKey.value.temporaryCrosshairs].value
    );
    watch(enableTempCrosshairs, (enable) => {
      if (enable) toolStore.activateTemporaryCrosshairs();
      else toolStore.deactivateTemporaryCrosshairs();
    });

    const nameToShortcut = computed(() => {
      const keyMap = actionToKey.value;
      return {
        'Window & Level': keyMap.windowLevel,
        Pan: keyMap.pan,
        Zoom: keyMap.zoom,
        Crosshairs: keyMap.crosshairs,
        Select: keyMap.select,
        Paint: keyMap.paint,
        Rectangle: keyMap.rectangle,
        Polygon: keyMap.polygon,
        Ruler: keyMap.ruler,
        Crop: keyMap.crop,
      };
    });

    const setWindowLevel = (width: number, level: number) => {
      console.log('🎛️ Flutter setting WindowLevel:', width, level);

      const viewID = viewStore.activeView;
      const imageID = currentImageID.value;

      if (!imageID || !viewID) {
        console.warn('❌ No active image or view for WL');
        return;
      }

      console.log(`🎯 Applying WL to view: ${viewID}, image: ${imageID}`);
      windowingStore.updateConfig(
        viewID,
        imageID,
        {
          width: width,
          level: level,
          useAuto: false
        },
        true
      );
    };

    const getCurrentWindowLevel = () => {
      const viewID = viewStore.activeView;
      const imageID = currentImageID.value;

      if (!imageID || !viewID) {
        console.warn('❌ No active image or view for WL get');
        return { width: 400, level: 40 };
      }

      const config = windowingStore.getConfig(viewID, imageID);
      const width = config?.width || 400;
      const level = config?.level || 40;

      console.log('🎛️ Current WL values:', { width, level });
      return { width, level };
    };

    const sendInitialWindowingToFlutter = () => {
      const viewID = viewStore.activeView;
      const imageID = currentImageID.value;

      if (!viewID || !imageID) return;

      console.log('🔍 Sending initial WL values to Flutter...');

      const trySendValues = (attempt: number, delay: number) => {
        setTimeout(() => {
          const config = windowingStore.getConfig(viewID, imageID);

          if (config && config.width && config.level && (config.width !== 400 || config.level !== 40)) {
            console.log(`🎯 Sending real WL values to Flutter (attempt ${attempt}):`, config);

            if (window.WindowingChannel) {
              window.WindowingChannel.postMessage(JSON.stringify({
                type: 'INITIAL_WL_FROM_VOLVIEW',
                width: config.width,
                level: config.level,
                seriesId: imageID
              }));
            }
          } else if (attempt < 5) {
            console.log(`🔄 Attempt ${attempt}: VolView not ready yet, waiting...`);
            trySendValues(attempt + 1, delay * 1.5);
          } else {
            console.warn('⚠️ Could not get real WL values from VolView after 5 attempts');
          }
        }, delay);
      };

      trySendValues(1, 500);
    };

    const setupWindowingObserver = () => {
      console.log('🔍 Setting up windowing observer...');

      let currentViewID: string | null = null;
      let currentImageID: string | null = null;
      let unwatch: (() => void) | null = null;

      const setupConfigWatcher = () => {
        if (unwatch) {
          unwatch();
          unwatch = null;
        }

        if (!currentViewID || !currentImageID) return;

        console.log(`👀 Observing windowing for view: ${currentViewID}, image: ${currentImageID}`);

        unwatch = watch(
          () => windowingStore.getConfig(currentViewID!, currentImageID!),
          (newConfig, oldConfig) => {
            if (newConfig && oldConfig &&
                (newConfig.width !== oldConfig.width || newConfig.level !== oldConfig.level)) {

              console.log('🔄 Windowing changed in VolView:', newConfig);

              if (window.WindowingChannel) {
                window.WindowingChannel.postMessage(JSON.stringify({
                  type: 'WINDOW_LEVEL_UPDATED',
                  width: newConfig.width,
                  level: newConfig.level
                }));
              } else {
                console.warn('⚠️ WindowingChannel not available');
              }
            }
          },
          { deep: true, immediate: false }
        );
      };

      watch(() => viewStore.activeView, (newViewID) => {
        if (newViewID && newViewID !== currentViewID) {
          currentViewID = newViewID;
          console.log(`🎯 Active view changed to: ${newViewID}`);
          setupConfigWatcher();
        }
      }, { immediate: true });

      watch(currentImageID, (newImageID) => {
        if (newImageID && newImageID !== currentImageID) {
          currentImageID = newImageID;
          console.log(`🎯 Current image changed to: ${newImageID}`);
          setupConfigWatcher();
        }
      }, { immediate: true });
    };

    window.sendZoomToFlutter = (zoomValue: number) => {
      console.log('📤 Sending zoom to Flutter:', zoomValue);
      if (window.SliceSliderChannel) {
        const payload = { type: 'zoomChanged', zoom: zoomValue };
        window.SliceSliderChannel.postMessage(JSON.stringify(payload));
      } else {
        window.postMessage(
          { type: 'zoomChanged', zoom: zoomValue },
          '*'
        );
      }
    };

    const setupZoomInterception = () => {
      let lastScale: number | null = null;
      const checkZoomChanges = () => {
        try {
          const viewSpec = viewStore.viewByID[viewStore.activeView];
          if (viewSpec) {
            const viewProxy = viewSpec.proxy || viewSpec.viewProxy || viewSpec.view;
            if (viewProxy && viewProxy.getCamera) {
              const camera = viewProxy.getCamera();
              if (camera && camera.getParallelScale) {
                const currentScale = camera.getParallelScale();
                if (lastScale !== null && Math.abs(currentScale - lastScale) > 0.001) {
                  const zoomValue = Math.max(0.5, Math.min(5.0, 100 / currentScale));
                  console.log('🔄 Zoom changed:', zoomValue);
                  window.sendZoomToFlutter?.(zoomValue);
                }
                lastScale = currentScale;
              }
            }
          }
        } catch (err) {
          console.log('⚠️ Zoom monitoring error:', err);
        }
      };
      setInterval(checkZoomChanges, 500);
      console.log('✅ Zoom interception active');
    };

    window.repairInteractions = () => {
      try {
        const canvas = document.querySelector('canvas');
        if (canvas) {
          canvas.style.pointerEvents = 'auto';
          canvas.focus();
        }
        const overlays = document.querySelectorAll('.overlay, .vtk-container-overlay, .view-overlay');
        overlays.forEach((el) => {
          (el as HTMLElement).style.pointerEvents = 'none';
        });
        console.log('✅ Interactions repaired');
      } catch (err) {
        console.error('❌ Error repairInteractions', err);
      }
    };

    window.setCurrentTool = (tool: Tools) => {
      console.log('🔧 setCurrentTool:', tool);
      const toolsWithoutMultiInteraction = ['Zoom', 'Pan', 'WindowLevel'];

      if (toolsWithoutMultiInteraction.includes(tool)) {
        if (window.enableControlsMode) window.enableControlsMode();
        if (window.disableAutoInteractions) window.disableAutoInteractions();
      } else {
        if (window.disableControlsMode) window.disableControlsMode();
        if (window.enableAutoInteractions) window.enableAutoInteractions();
        if (tool === 'WindowLevel' && window.setupWindowingInteractions) {
          console.log('🎛️ Setting up windowing');
          window.setupWindowingInteractions();
        }
      }

      toolStore.setCurrentTool(tool);

      setTimeout(() => {
        if (toolStore.currentTool !== tool) {
          console.log('♻️ Tool reset detected → reapply');
          toolStore.setCurrentTool(tool);
        }
      }, 200);

      setTimeout(() => window.repairInteractions?.(), 50);
    };

    watch(currentImageID, (newID, oldID) => {
      if (!newID || newID === oldID) return;
      console.log('🧩 Série changée :', oldID, '→', newID);

      setTimeout(() => {
        sendInitialWindowingToFlutter();
      }, 0);

      setTimeout(() => {
        try {
          const payload = {
            type: 'SERIES_LOADED',
            seriesId: newID,
          };
          if (window.WindowingChannel) {
            window.WindowingChannel.postMessage(JSON.stringify(payload));
          } else {
            console.warn('⚠️ WindowingChannel non disponible');
          }
        } catch (err) {
          console.error('❌ Erreur lors de l\'envoi SERIES_LOADED', err);
        }
      }, 0);
    });

    onMounted(() => {
      if (typeof window !== 'undefined') {
        window.setWindowLevel = setWindowLevel;
        window.getCurrentWindowLevel = getCurrentWindowLevel;
        console.log('✅ Flutter windowing functions initialized in Controls');

        setTimeout(() => {
          const current = window.getCurrentWindowLevel();
          console.log('🧪 Test WL on mount:', current);
        }, 100);
      }

      setupZoomInterception();
      setupWindowingObserver();
      console.log('✅ All observers initialized');
    });

    return {
      currentTool,
      setCurrentTool: toolStore.setCurrentTool,
      noCurrentImage,
      isObliqueLayout,
      Tools,
      paintMenu,
      cropMenu,
      windowingMenu,
      nameToShortcut,
    };
  },
});
</script>
